import Box from '@mui/material/Box';
import AppButton from './AppButton';
import { Shimmer } from './Shimmer';

/**
 * One place to render "still loading" and "that failed" for an RTK Query result,
 * so every panel behaves the same when the API is slow or down.
 *
 * Wrap content: <QueryState query={q}>{(data) => …}</QueryState>
 *
 * Pass `skeleton` with the shape this panel is going to be — a KPI strip, a table,
 * a chart — so the placeholder occupies the same space the data will. Without one
 * it falls back to a plain shimmering block of `height`.
 */
export default function QueryState({ query, height = 120, skeleton, children, empty }) {
  const { data, error, isLoading, isError, refetch } = query;

  // `isLoading` is only true for the *first* fetch of a cache entry — a skipped or
  // not-yet-started query reports false with no data, so check for data too rather
  // than handing `undefined` to the success branch.
  if (isLoading || (!data && !isError)) return skeleton || <LoadingBlock height={height} />;

  if (isError) {
    return (
      <Box
        sx={{
          border: '1px dashed #e9c9c9',
          background: '#fbeeee',
          borderRadius: '12px',
          p: '16px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 650, color: 'var(--crit)' }}>Couldn't load this</Box>
          <Box sx={{ fontSize: 13, color: 'var(--ink-2)', mt: 0.25 }}>
            {error?.message || 'Something went wrong.'}
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        <AppButton onClick={refetch}>Try again</AppButton>
      </Box>
    );
  }

  if (empty && empty(data)) {
    return (
      <Box sx={{ color: 'var(--muted)', fontSize: 13, p: '18px 0' }}>
        Nothing to show for this period.
      </Box>
    );
  }

  return children(data);
}

export function LoadingBlock({ height = 120 }) {
  return <Shimmer height={height} radius={12} />;
}

/** Dims a panel while a refetch is in flight, instead of blanking it. */
export function Refetching({ active, children }) {
  return (
    <Box sx={{ opacity: active ? 0.55 : 1, transition: 'opacity .15s' }}>{children}</Box>
  );
}
